import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, adminApi, templateToDraft,
  type AdminTemplate, type AdminTenant, type TemplateDraft,
} from '@wa/shared';
import { Page, Empty } from '@/components/Primitives';
import { TemplateBuilder } from '@/components/TemplateBuilder';

const BLANK: TemplateDraft = {
  name: '',
  language: 'en_US',
  category: 'UTILITY',
  headerFormat: 'NONE',
  body: '',
  bodyExamples: [],
  buttons: [],
};

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; template: AdminTemplate };

/**
 * Authoring templates for a client, so nobody opens WhatsApp Manager.
 *
 * The tenant is chosen first and deliberately stays visible: a template is
 * submitted under that client's WABA and counts against their quality rating, so
 * "which client am I doing this to" should never be a question.
 */
export function TemplatesPage() {
  const queryClient = useQueryClient();
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [draft, setDraft] = useState<TemplateDraft>(BLANK);
  const [error, setError] = useState<string | null>(null);

  const tenants = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: adminApi.tenants,
    staleTime: 300_000,
  });

  // The first tenant is selected once the list arrives, so the screen is never
  // an empty dropdown above an empty table.
  const selectedTenant = tenantId ?? tenants.data?.[0]?.id ?? null;

  const templates = useQuery({
    queryKey: ['admin-templates', selectedTenant],
    queryFn: () => adminApi.templates(selectedTenant!),
    enabled: selectedTenant !== null,
  });

  function done() {
    setMode({ kind: 'list' });
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['admin-templates'] });
  }

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'That did not reach WhatsApp.');
  }

  const create = useMutation({
    mutationFn: () => adminApi.createTemplate(selectedTenant!, draft),
    onSuccess: done,
    onError: fail,
  });

  const edit = useMutation({
    mutationFn: (id: number) => adminApi.editTemplate(id, draft),
    onSuccess: done,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: adminApi.deleteTemplate,
    onSuccess: done,
    onError: fail,
  });

  const sync = useMutation({
    mutationFn: () => adminApi.syncTemplates(selectedTenant!),
    onSuccess: done,
    onError: fail,
  });

  const saving = create.isPending || edit.isPending;

  function open(next: Mode, from?: AdminTemplate) {
    setDraft(from ? templateToDraft(from) : BLANK);
    setError(null);
    setMode(next);
  }

  function duplicate(template: AdminTemplate) {
    // A duplicate is almost always the same message in another language, so the
    // name is kept and the language cleared to force a deliberate choice.
    setDraft({ ...templateToDraft(template), language: '' });
    setError(null);
    setMode({ kind: 'create' });
  }

  if (mode.kind !== 'list') {
    const isEditing = mode.kind === 'edit';

    return (
      <Page
        title={isEditing ? `Edit ${mode.template.name}` : 'New template'}
        subtitle={
          isEditing
            ? 'Meta re-reviews an edited template. Approved templates allow ten edits per month, or one a day.'
            : 'Submitted to WhatsApp for approval under this client’s account.'
        }
      >
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => (isEditing ? edit.mutate(mode.template.id) : create.mutate())}
            className="tap rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-(--color-brand-ink) disabled:opacity-40"
          >
            {saving ? 'Submitting…' : isEditing ? 'Submit the edit' : 'Submit for approval'}
          </button>
          <button
            type="button"
            onClick={() => setMode({ kind: 'list' })}
            className="tap rounded-lg border border-(--color-line) px-4 py-2 text-body text-(--color-muted) hover:text-(--color-ink)"
          >
            Cancel
          </button>
        </div>

        {error && (
          <p role="alert" className="mb-4 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {error}
          </p>
        )}

        <TemplateBuilder draft={draft} onChange={setDraft} isEditing={isEditing} />
      </Page>
    );
  }

  return (
    <Page
      title="Templates"
      subtitle="Create and manage a client’s WhatsApp templates without leaving the platform."
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={selectedTenant ?? ''}
          onChange={(e) => setTenantId(Number(e.target.value))}
          aria-label="Client"
          className="tap min-w-0 rounded-lg border border-(--color-line) bg-(--color-sunken) px-3 py-2 text-body outline-none focus:border-(--color-brand)"
        >
          {(tenants.data ?? []).map((t: AdminTenant) => (
            <option key={t.id} value={t.id}>{t.companyName}</option>
          ))}
        </select>

        <button
          type="button"
          disabled={selectedTenant === null}
          onClick={() => open({ kind: 'create' })}
          className="tap rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-(--color-brand-ink) disabled:opacity-40"
        >
          New template
        </button>

        <button
          type="button"
          disabled={selectedTenant === null || sync.isPending}
          onClick={() => sync.mutate()}
          className="tap rounded-lg border border-(--color-line) px-3 py-2 text-body text-(--color-muted) hover:text-(--color-ink) disabled:opacity-40"
        >
          {sync.isPending ? 'Refreshing…' : 'Refresh from Meta'}
        </button>
      </div>

      {error && (
        <p role="alert" className="mb-4 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {templates.isPending && <Empty>Loading templates…</Empty>}
      {templates.isSuccess && templates.data.length === 0 && (
        <Empty>No templates on this client’s account yet.</Empty>
      )}

      {/* Cards rather than a table: a template has a body worth reading, and a
          body does not survive being squeezed into a table cell on a phone. */}
      <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
        {(templates.data ?? []).map((template) => (
          <article
            key={template.id}
            className={`flex min-w-0 flex-col rounded-lg border border-(--color-line) bg-(--color-surface) p-3 ${
              template.status === 'DELETED' ? 'opacity-55' : ''
            }`}
          >
            <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <h3 className="min-w-0 flex-1 truncate text-small font-semibold">{template.name}</h3>
              <span className="shrink-0 text-micro text-(--color-muted) uppercase">{template.language}</span>
              <StatusPill status={template.status} />
            </header>

            <p className="mt-1.5 line-clamp-3 min-w-0 text-small break-words text-(--color-muted)">
              {template.bodyText ?? '—'}
            </p>

            <p className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-micro text-(--color-muted)">
              <span>{template.category ?? 'uncategorised'}</span>
              {template.bodyVariables > 0 && <span>{template.bodyVariables} value(s)</span>}
              {template.authoredBy && <span className="truncate">by {template.authoredBy}</span>}
            </p>

            {template.rejectedReason && (
              <p className="mt-2 rounded border border-(--color-bad) px-2 py-1 text-micro text-(--color-bad)">
                Meta said: {template.rejectedReason}
              </p>
            )}

            {template.status !== 'DELETED' && (
              <div className="mt-3 flex flex-wrap gap-1.5 border-t border-(--color-line) pt-2.5">
                <Action onClick={() => open({ kind: 'edit', template }, template)}>Edit</Action>
                <Action onClick={() => duplicate(template)}>Duplicate</Action>
                <Action
                  tone="bad"
                  onClick={() => remove.mutate(template.id)}
                  disabled={remove.isPending}
                >
                  Delete
                </Action>
              </div>
            )}
          </article>
        ))}
      </div>
    </Page>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'APPROVED' ? 'border-(--color-good) text-(--color-good)'
    : status === 'REJECTED' || status === 'DELETED' ? 'border-(--color-bad) text-(--color-bad)'
    : 'border-(--color-warn) text-(--color-warn)';

  return (
    <span className={`shrink-0 rounded border px-1.5 py-px text-micro tracking-wide uppercase ${tone}`}>
      {status}
    </span>
  );
}

function Action({ children, onClick, disabled, tone }: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'bad';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`tap rounded border border-(--color-line) px-2.5 py-1 text-small disabled:opacity-40 ${
        tone === 'bad'
          ? 'text-(--color-muted) hover:border-(--color-bad) hover:text-(--color-bad)'
          : 'text-(--color-muted) hover:text-(--color-ink)'
      }`}
    >
      {children}
    </button>
  );
}
